/**
 * Fragment-based href convention emitted by the AI agent to trigger UI actions
 * inside the chat (open Blip support chat, etc.). Surfaces that render AI
 * markdown listen for `COBLI_ACTION_EVENT` to react (e.g. close their own chat
 * panel before Blip takes over).
 *
 * Scheme: `#cobli-<action>` (fragment-only).
 *
 * Why a fragment and not a custom `cobli://` URL scheme: Streamdown sanitizes
 * markdown links through `rehype-harden`, which blocks any non-standard URL
 * protocol (it replaces the `<a>` with a `<span> … [blocked]`). A `cobli://`
 * link therefore never renders as a clickable anchor and the interceptor's
 * onClick never fires. Fragment-only hrefs (`#…`) are explicitly allowed by
 * harden, so the anchor survives and `AIChatMarkdown.tsx` can intercept it.
 *
 * To add a new action:
 *  1. Add the href to `COBLI_LINK_HREF` below (keep the `#cobli-` prefix).
 *  2. Handle it in `AIChatMarkdown.tsx`'s link interceptor.
 *  3. (Optional) Have any chat surface that should close itself listen for the
 *     `COBLI_ACTION_EVENT` and inspect `detail.action`.
 */
export const COBLI_LINK_SCHEME = '#cobli-';

export const COBLI_LINK_HREF = {
  /** Closes the current AI chat surface and opens the Blip human-support chat. */
  OPEN_BLIP_CHAT: '#cobli-open-blip-chat',
} as const;

export type CobliLinkAction = 'open-blip-chat';

export const COBLI_ACTION_EVENT = 'cobli:ai-link-action';

export interface CobliActionEventDetail {
  action: CobliLinkAction;
}

export const dispatchCobliAction = (action: CobliLinkAction) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<CobliActionEventDetail>(COBLI_ACTION_EVENT, { detail: { action } }));
};

export const isCobliLink = (href: string | undefined | null): href is string => Boolean(href?.startsWith(COBLI_LINK_SCHEME));
